import React, { useLayoutEffect, useRef } from 'react';
import { Input } from '../../components/ui/input';
import { cn } from '@/lib/utils';
import { PHONE_PLACEHOLDER, phoneDigits, formatPhoneInput } from './phone';
import { afterEdit, caretAfterNth } from './caret';

// The mask's country-code prefix. It is display only: never stored, and its
// '1' must not be counted as a typed digit.
const PREFIX = '+1 ';
const PREFIX_RE = /^\+?1\s*(?=\()/;
const stripPrefix = (s) => s.replace(PREFIX_RE, '');

// US-only phone input (ticket 125). Displays '+1 (512) 555-0101' while typing
// (the country code is part of the mask, not the data); the value it
// stores/emits is digits-only, 10 digits max ('5125550101'), matching backend
// normalize_phone(). International numbers are out of scope on purpose: a
// leading +1 / 1 is dropped, anything else non-numeric is ignored. No maxLength
// so a pasted '+1 (512) 555-0101 ext 4' is never truncated before normalizing.
export default function PhoneInput({ value, onChange, className, ...rest }) {
  const ref = useRef(null);
  const pendingCaret = useRef(null);

  const handle = (e) => {
    const raw = e.target.value;
    const caretRaw = e.target.selectionStart ?? raw.length;
    const prefix = raw.match(PREFIX_RE)?.[0] ?? '';
    const body = raw.slice(prefix.length);
    const caret = Math.max(0, caretRaw - prefix.length);
    const { sig, before } = afterEdit(stripPrefix(formatPhoneInput(value)), body, caret);
    const digits = phoneDigits(sig);
    const nextBody = stripPrefix(formatPhoneInput(digits));
    pendingCaret.current = nextBody
      ? PREFIX.length + caretAfterNth(nextBody, Math.min(before, digits.length))
      : 0;
    onChange(digits);
  };

  useLayoutEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      const p = pendingCaret.current;
      try {
        ref.current.setSelectionRange(p, p);
      } catch (err) {
        // some input types don't support selection ranges — never crash a form over a caret
      }
      pendingCaret.current = null;
    }
  });

  return (
    <Input
      autoComplete="tel"
      placeholder={PHONE_PLACEHOLDER}
      {...rest}
      type="tel"
      inputMode="tel"
      value={formatPhoneInput(value)}
      onChange={handle}
      className={cn(className)}
      ref={ref}
    />
  );
}
